import * as config from 'config';
import type { Channel } from 'pusher-js';
import Pusher from 'pusher-js';
import create from 'zustand';
import { getStoredExpertAuthData } from 'utils/expertAuth';

type Store = {
  channel: Channel | null;
  createOrUpdate: (accountantId: string) => void;
  destroy: () => void;
  renew: () => void;
};

const getChannelName = (id: string) => `private-${id}`;

const createPusherInstance = () => {
  const expertAuth = getStoredExpertAuthData();
  const accessToken = expertAuth?.auth?.access_token;

  return new Pusher(config.pusher.key, {
    authEndpoint: `${config.accountableAPI.baseUrl}/v3/access/accountants/pusher/auth`,
    cluster: 'eu',
    auth: {
      params: {},
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  });
};

const pusherChannelStore = create<Store>((set, get) => ({
  channel: null,
  createOrUpdate: (accountantId: string) => {
    const channelName = getChannelName(accountantId);
    const channel = get().channel;

    if (channel && channel.name === channelName) return;

    get().destroy();

    const instance = createPusherInstance();

    set({ channel: instance.subscribe(channelName) });
  },
  destroy: () => {
    const channel = get().channel;
    if (!channel) return;

    channel.unbind_all();
    channel.unsubscribe();
    channel.disconnect();

    set({ channel: null });
  },
  renew: () => {
    const currentChannel = get().channel;
    if (!currentChannel) return;

    const channelName = currentChannel.name;

    get().destroy();

    const instance = createPusherInstance();

    set({ channel: instance.subscribe(channelName) });
  },
}));

type ExpertPusherChannelController = {
  useChannel: () => Channel | null;
  createOrUpdate: (accountantId: string) => void;
  destroy: () => void;
  renew: () => void;
};

const expertPusherChannelController: ExpertPusherChannelController = {
  useChannel: (): Channel | null =>
    pusherChannelStore((state) => state.channel),
  createOrUpdate: (accountantId: string): void =>
    pusherChannelStore.getState().createOrUpdate(accountantId),
  destroy: (): void => pusherChannelStore.getState().destroy(),
  renew: (): void => pusherChannelStore.getState().renew(),
};

export default expertPusherChannelController;
